import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Download, Search, Check, X, Eye, EyeOff, Edit2, RefreshCw, Shield, Mail, Save, Clock, Server, Database, AlertTriangle, CheckCircle, XCircle, FileText, Settings, TestTube, Power } from 'lucide-react';
import { Textarea } from '../../components/ui/textarea';

import { adminAPI, emailAPI, emailIngestionAPI, emailAccountsAPI } from '../../services/api';

export function EmailSettingsPage() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [syncInterval, setSyncInterval] = useState(90);
  const [savingInterval, setSavingInterval] = useState(false);
  const [newAccount, setNewAccount] = useState({
    email: '',
    app_password: '',
    host: 'imap.gmail.com',
    port: 993,
    smtp_host: 'smtp.gmail.com',
    smtp_port: 587,
    smtp_use_tls: true
  });

  useEffect(() => {
    fetchAccounts();
    fetchSyncInterval();
  }, []);

  const fetchSyncInterval = async () => {
    try {
      const res = await emailIngestionAPI.getSyncInterval();
      setSyncInterval(res.data.interval_minutes);
    } catch (err) {}
  };

  const handleSaveSyncInterval = async () => {
    if (syncInterval < 15 || syncInterval > 1440) {
      toast.error('Interval must be between 15 and 1440 minutes');
      return;
    }
    setSavingInterval(true);
    try {
      await emailIngestionAPI.setSyncInterval(syncInterval);
      toast.success(`Sync interval set to ${syncInterval} minutes`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update sync interval');
    } finally {
      setSavingInterval(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await emailAccountsAPI.list();
      setAccounts(response.data);
    } catch (error) {
      toast.error('Failed to fetch email accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAccount = async () => {
    if (!newAccount.email || !newAccount.app_password) {
      toast.error('Email and App Password are required');
      return;
    }

    try {
      await emailAccountsAPI.create(newAccount);
      toast.success('Email account added successfully');
      setAddDialogOpen(false);
      setNewAccount({ 
        email: '', 
        app_password: '', 
        host: 'imap.gmail.com', 
        port: 993,
        smtp_host: 'smtp.gmail.com',
        smtp_port: 587,
        smtp_use_tls: true
      });
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add email account');
    }
  };

  const handleDeleteAccount = async (id) => {
    if (!window.confirm('Are you sure you want to delete this email account?')) return;

    try {
      await emailAccountsAPI.delete(id);
      toast.success('Email account deleted');
      fetchAccounts();
    } catch (error) {
      toast.error('Failed to delete email account');
    }
  };

  const handleToggleAccount = async (id) => {
    try {
      const response = await emailAccountsAPI.toggle(id);
      toast.success(`Email account ${response.data.status === 'active' ? 'activated' : 'deactivated'}`);
      fetchAccounts();
    } catch (error) {
      toast.error('Failed to toggle email account');
    }
  };

  const handleTestAccount = async (id) => {
    setTestingId(id);
    try {
      const response = await emailAccountsAPI.test(id);
      if (response.data.success) {
        toast.success('Connection successful!');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Connection test failed');
    } finally {
      setTestingId(null);
    }
  };

  const handleManualSync = async () => {
    setSyncing(true);
    try {
      await emailIngestionAPI.sync();
      toast.success('Email sync started in background');
    } catch (error) {
      toast.error('Failed to start email sync');
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-heading uppercase flex items-center gap-2">
            <Mail className="h-6 w-6" />
            Email Ingestion Settings
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage Gmail accounts for automatic RFQ ingestion
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleManualSync}
            disabled={syncing}
            data-testid="manual-sync-btn"
          >
            {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Sync Now
          </Button>
          <Button onClick={() => setAddDialogOpen(true)} data-testid="add-email-btn">
            <Mail className="h-4 w-4 mr-2" />
            Add Email Account
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-blue-800">Gmail App Passwords Required</p>
              <p className="text-blue-700 mt-1">
                To connect Gmail accounts, you need to generate 16-digit App Passwords from your Google Account settings.
                Go to <strong>Google Account → Security → 2-Step Verification → App passwords</strong>.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sync Interval Configuration */}
      <Card className="card-dashboard">
        <CardHeader>
          <CardTitle className="text-lg font-heading uppercase flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Auto-Sync Interval
          </CardTitle>
          <CardDescription>Configure how frequently the system checks for new emails across all connected accounts</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-4">
            <div className="space-y-2 w-48">
              <Label>Interval (minutes)</Label>
              <Input
                type="number"
                min={15}
                max={1440}
                value={syncInterval}
                onChange={(e) => setSyncInterval(parseInt(e.target.value) || 90)}
                data-testid="sync-interval-input"
              />
              <p className="text-xs text-muted-foreground">Min: 15 min, Max: 1440 min (24h)</p>
            </div>
            <Button
              onClick={handleSaveSyncInterval}
              disabled={savingInterval}
              className="btn-primary"
              data-testid="save-sync-interval-btn"
            >
              {savingInterval ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
              Save
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Email Accounts Table */}
      <Card className="card-dashboard">
        <CardHeader>
          <CardTitle className="text-lg font-heading uppercase">Connected Email Accounts</CardTitle>
          <CardDescription>Emails are auto-synced every {syncInterval} minutes</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : accounts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No email accounts configured</p>
              <p className="text-sm mt-1">Add a Gmail account to start ingesting RFQs</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last Sync</TableHead>
                    <TableHead>Emails Processed</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {accounts.map((account) => (
                    <TableRow key={account.id}>
                      <TableCell className="font-mono">{account.email}</TableCell>
                      <TableCell>
                        <Badge className={
                          account.status === 'active' ? 'badge-success' :
                          account.status === 'error' ? 'badge-error' :
                          'badge-warning'
                        }>
                          {account.status}
                        </Badge>
                        {account.last_error && (
                          <p className="text-xs text-red-500 mt-1">{account.last_error}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{formatDate(account.last_sync_at)}</TableCell>
                      <TableCell>{account.emails_processed || 0}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleTestAccount(account.id)}
                            disabled={testingId === account.id}
                            title="Test Connection"
                          >
                            {testingId === account.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <TestTube className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleToggleAccount(account.id)}
                            title={account.status === 'active' ? 'Deactivate' : 'Activate'}
                          >
                            <Power className={`h-4 w-4 ${account.status === 'active' ? 'text-green-500' : 'text-gray-400'}`} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteAccount(account.id)}
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Email Account Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Gmail Account</DialogTitle>
            <DialogDescription>
              Configure Gmail for both receiving (IMAP) and sending (SMTP) emails
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">Gmail Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="inquiry@yourcompany.com"
                value={newAccount.email}
                onChange={(e) => setNewAccount(prev => ({ ...prev, email: e.target.value }))}
                data-testid="new-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="app_password">App Password</Label>
              <Input
                id="app_password"
                type="password"
                placeholder="xxxx xxxx xxxx xxxx"
                value={newAccount.app_password}
                onChange={(e) => setNewAccount(prev => ({ ...prev, app_password: e.target.value }))}
                data-testid="new-password-input"
              />
              <p className="text-xs text-muted-foreground">
                Generate at: Google Account → Security → App passwords
              </p>
            </div>
            
            {/* SMTP Configuration Section */}
            <div className="pt-4 border-t">
              <p className="text-sm font-medium mb-3">SMTP Settings (for sending emails)</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="smtp_host" className="text-xs">SMTP Host</Label>
                  <Input
                    id="smtp_host"
                    type="text"
                    value={newAccount.smtp_host}
                    onChange={(e) => setNewAccount(prev => ({ ...prev, smtp_host: e.target.value }))}
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="smtp_port" className="text-xs">SMTP Port</Label>
                  <Input
                    id="smtp_port"
                    type="number"
                    value={newAccount.smtp_port}
                    onChange={(e) => setNewAccount(prev => ({ ...prev, smtp_port: parseInt(e.target.value) || 587 }))}
                    className="h-8 text-sm"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Default: smtp.gmail.com:587 (TLS). Use port 465 for SSL.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddAccount} data-testid="save-email-btn">
              Add Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ============== DATA BACKUPS PAGE ==============


export default EmailSettingsPage;
